/**
 * SignupBanner - Prominent top-of-page banner promoting $49 lifetime signup.
 * Sits above the Header so it's the first thing visitors see.
 */
import { Sparkles, ArrowRight } from 'lucide-react';

export default function SignupBanner({ onSignUpClick }) {
  return (
    <>
      <style>{`
        @keyframes p4s-banner-shine {
          0%   { transform: translateX(-150%) skewX(-20deg); }
          60%  { transform: translateX(150%) skewX(-20deg); }
          100% { transform: translateX(150%) skewX(-20deg); }
        }
        @keyframes p4s-banner-arrow {
          0%, 100% { transform: translateX(0); }
          50%      { transform: translateX(4px); }
        }

        .p4s-signup-banner {
          position: relative;
          z-index: 110;
          color: #fff;
          padding: 0.6rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
          font-size: 0.92rem;
          font-weight: 500;
          letter-spacing: 0.01em;
          background:
            radial-gradient(circle at 25% 50%, rgba(56, 189, 248, 0.45), transparent 55%),
            radial-gradient(circle at 75% 50%, rgba(45, 212, 191, 0.4), transparent 55%),
            linear-gradient(120deg, #0c2470 0%, #1e40af 50%, #0c2470 100%);
          overflow: hidden;
          isolation: isolate;
        }

        /* The shine sweep — diagonal white gradient that travels across */
        .p4s-signup-banner::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 60%;
          height: 100%;
          background: linear-gradient(
            100deg,
            transparent 0%,
            transparent 35%,
            rgba(255, 255, 255, 0.35) 50%,
            transparent 65%,
            transparent 100%
          );
          transform: translateX(-150%) skewX(-20deg);
          animation: p4s-banner-shine 4.5s ease-in-out infinite;
          animation-delay: 1s;
          pointer-events: none;
          z-index: 0;
        }

        .p4s-signup-banner__text,
        .p4s-signup-banner__cta {
          position: relative;
          z-index: 1;
        }

        .p4s-signup-banner__text {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        }
        .p4s-signup-banner__highlight {
          background: rgba(255, 255, 255, 0.22);
          padding: 0.12rem 0.55rem;
          border-radius: 999px;
          font-weight: 700;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          border: 1px solid rgba(255, 255, 255, 0.18);
        }
        .p4s-signup-banner__cta {
          background: #fff;
          color: #0030b3;
          padding: 0.32rem 1rem;
          border-radius: 999px;
          border: none;
          font-weight: 700;
          font-size: 0.85rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .p4s-signup-banner__cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28);
        }
        .p4s-signup-banner__cta:hover .p4s-signup-banner__arrow {
          animation: p4s-banner-arrow 0.8s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .p4s-signup-banner::before { animation: none; display: none; }
        }
        @media (max-width: 540px) {
          .p4s-signup-banner {
            font-size: 0.85rem;
            padding: 0.5rem 0.9rem;
            gap: 0.55rem;
          }
          .p4s-signup-banner__highlight {
            display: none;
          }
        }
      `}</style>
      <div
        className="p4s-signup-banner"
        data-testid="signup-banner"
        role="region"
        aria-label="Request a trial account"
      >
        <span className="p4s-signup-banner__text">
          <Sparkles size={18} aria-hidden="true" />
          <span>Try Practice4SAT FREE for 3 days — request a free trial account</span>
        </span>
        <button
          type="button"
          className="p4s-signup-banner__cta"
          onClick={onSignUpClick}
          data-testid="signup-banner-cta"
        >
          Request Trial Account
          <ArrowRight size={16} className="p4s-signup-banner__arrow" aria-hidden="true" />
        </button>
      </div>
    </>
  );
}
